import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import type { UpdateMipKycSettingDto } from '@dealstack/shared/lib/generated';
import {
	CERTIFICATION_FLAG_BY_PROOF,
	isAnyCertificationRequired,
	proofToggleFlags,
	type KycCertificationFlag,
	type KycCertificationRequirementFlags,
	type KycProofFlag,
	type KycProofRequirementFlags,
} from '@dealstack/shared/lib/common/business/kycCertification';
import { useMipKycSetting } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useKycSetting.ts';
import { updateMipKycSetting } from '@dealstack/frontend/shared/dataExchange/services/kyc-setting.service.ts';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';

export type RequirementFlag = KycProofFlag | KycCertificationFlag;
type RequirementFlags = Partial<Record<RequirementFlag, boolean>>;

/** `CERTIFICATION_FLAG_BY_PROOF` is keyed by proof flag, so membership identifies one. */
const isProofFlag = (flag: RequirementFlag): flag is KycProofFlag =>
	flag in CERTIFICATION_FLAG_BY_PROOF;

export const useKYCRequirementsController = () => {
	const { mipId } = useRequiredParams('mipId');
	const queryClient = useQueryClient();
	const { data: kycSetting } = useMipKycSetting(mipId);

	const [showHowToProvideDocsModal, setShowHowToProvideDocsModal] = useState(false);
	// Answers the switch immediately; the query result takes over once it refetches.
	const [optimistic, setOptimistic] = useState<RequirementFlags>({});

	/**
	 * Drops this save's keys, handing those switches back to the server's values. On success the
	 * refetch has already landed them — including any coercion the repository applied on write;
	 * on failure they are the values that never changed. Left in place the overlay shadows the
	 * real setting for the rest of the page's life.
	 */
	const releaseOverlay = (changes: RequirementFlags) =>
		setOptimistic((current) =>
			Object.fromEntries(Object.entries(current).filter(([flag]) => !(flag in changes)))
		);

	// On the mutation rather than per `mutate` call: in TanStack v5 a per-call callback fires only
	// for the latest call, so two quick toggles where the first fails left the first switch
	// showing a value that never persisted.
	const updateCertificationRequest = useMutation({
		mutationFn: (data: UpdateMipKycSettingDto) => updateMipKycSetting(data),
		onSuccess: async (_result, changes) => {
			await queryClient.invalidateQueries({
				queryKey: [API_PATHS.KYC_SETTING_MIP_GET, mipId],
			});
			releaseOverlay(changes);
		},
		onError: (_error, changes) => releaseOverlay(changes),
	});

	// A programme with no saved row behaves as the schema defaults, which collect every proof and
	// certify none. The fallback therefore differs by flag: arming certification here would show
	// three switches on that the stored row does not hold.
	const resolve = (flag: RequirementFlag) =>
		optimistic[flag] ?? kycSetting?.[flag] ?? isProofFlag(flag);

	const proofs: KycProofRequirementFlags = {
		isProofOfIdentityRequired: resolve('isProofOfIdentityRequired'),
		isProofOfAddressRequired: resolve('isProofOfAddressRequired'),
		isProofOfInvestmentVehicleRequired: resolve('isProofOfInvestmentVehicleRequired'),
	};

	const certification: KycCertificationRequirementFlags = {
		isProofOfIdentityCertificationRequired: resolve('isProofOfIdentityCertificationRequired'),
		isProofOfAddressCertificationRequired: resolve('isProofOfAddressCertificationRequired'),
		isProofOfInvestmentVehicleCertificationRequired: resolve(
			'isProofOfInvestmentVehicleCertificationRequired'
		),
	};

	/** Answers the switch immediately; {@link releaseOverlay} hands it back when the save settles. */
	const save = (changes: RequirementFlags) => {
		setOptimistic((current) => ({ ...current, ...changes }));
		updateCertificationRequest.mutate(changes);
	};

	const onCertificationChange = (flag: KycCertificationFlag, value: boolean) =>
		save({ [flag]: value });

	/**
	 * Sends the certification alongside its proof. The repository enforces the same invariant on
	 * write, but without sending it the switch would show a stale value until the query refetches.
	 */
	const onProofChange = (flag: KycProofFlag, value: boolean) =>
		save(proofToggleFlags(flag, value));

	// Only the flags this save is carrying, so one switch in flight does not grey out the rest.
	const inFlight = updateCertificationRequest.isPending
		? updateCertificationRequest.variables
		: undefined;
	const isSaving = (flag: RequirementFlag) => !!inFlight && flag in inFlight;

	return {
		showHowToProvideDocsModal,
		setShowHowToProvideDocsModal,
		proofs,
		certification,
		isAnyCertificationRequired: isAnyCertificationRequired(certification),
		onProofChange,
		onCertificationChange,
		isSaving,
	};
};
